import {
  ChartsTooltipContainer,
  chartsTooltipClasses,
  type ChartsTooltipProps,
  useAxesTooltip,
} from '@mui/x-charts/ChartsTooltip';

type LineChartTooltipProps = ChartsTooltipProps & {
  formatTooltipValue: (seriesKey: string, value: number | null) => string;
  hideTooltipSeriesLabel: boolean;
  tooltipLabelsByAxisValue: Map<string, string>;
};

const LineChartTooltip = ({
  classes,
  formatTooltipValue,
  hideTooltipSeriesLabel,
  sx,
  tooltipLabelsByAxisValue,
  ...props
}: LineChartTooltipProps): JSX.Element | null => {
  const tooltipData = useAxesTooltip();
  if (!tooltipData) return null;
  const paperClassName = `${classes?.paper ?? ''} flowqa-line-tooltip-paper`.trim();
  const tableClassName =
    `${chartsTooltipClasses.table} ${classes?.table ?? ''} flowqa-line-tooltip-table`.trim();
  const rowClassName = chartsTooltipClasses.row;
  const baseCellClassName =
    `${chartsTooltipClasses.cell} ${classes?.cell ?? ''} flowqa-line-tooltip-cell`.trim();
  const labelCellClassName = `${baseCellClassName} ${chartsTooltipClasses.labelCell}`.trim();
  const valueCellClassName = `${baseCellClassName} ${chartsTooltipClasses.valueCell}`.trim();

  return (
    <ChartsTooltipContainer classes={classes} sx={sx} {...props}>
      <div className={paperClassName}>
        {tooltipData.map(({ axisId, axisValue, axisFormattedValue, seriesItems }) => {
          const axisKey = typeof axisValue === 'string' ? axisValue : axisFormattedValue;
          const label = tooltipLabelsByAxisValue.get(axisKey) ?? axisFormattedValue;
          return (
            <table key={String(axisId)} className={tableClassName}>
              <thead>
                <tr>
                  <th className={labelCellClassName} colSpan={2}>
                    {label}
                  </th>
                </tr>
                <tr className="flowqa-line-tooltip-divider-row">
                  <td className="flowqa-line-tooltip-divider-cell" colSpan={2} />
                </tr>
              </thead>
              <tbody>
                {seriesItems.map((item, index) => {
                  const resolvedValue =
                    typeof item.formattedValue === 'string'
                      ? item.formattedValue
                      : typeof item.value === 'number'
                        ? formatTooltipValue(String(item.seriesId), item.value)
                        : '';
                  const isEmptyStateRow =
                    resolvedValue.length > 0 &&
                    (hideTooltipSeriesLabel || (item.formattedLabel ?? '').trim().length > 0) &&
                    !(typeof item.value === 'number' && Number.isFinite(item.value));

                  return (
                    <tr
                      key={`${axisId}-${item.formattedLabel ?? ''}-${index}`}
                      className={rowClassName}
                    >
                      <th className={labelCellClassName}>
                        <span className={chartsTooltipClasses.markContainer}>
                          <span
                            className={chartsTooltipClasses.mark}
                            style={{
                              backgroundColor:
                                typeof item.color === 'string' ? item.color : undefined,
                            }}
                          />
                        </span>
                        {isEmptyStateRow
                          ? ''
                          : hideTooltipSeriesLabel
                            ? ''
                            : (item.formattedLabel ?? '')}
                      </th>
                      <td className={valueCellClassName}>{resolvedValue}</td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          );
        })}
      </div>
    </ChartsTooltipContainer>
  );
};

export { LineChartTooltip };
